import { useState } from "react";
import { useQuery } from "react-query";
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  Card,
  CardContent,
  CardHeader,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Tooltip,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridFooterContainer,
} from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  formatDate_12h,
  overviewListItemStyles,
  formatDateWithoutTime,
  formatTimeWithAmPm,
} from "../../../services/Utils";
import {
  getVehicleServices,
  getServiceContractAuditLogs,
  getRoutingProxyLogs,
} from "../../../services/vehicle";
import moment from "moment";
import { grey } from "@mui/material/colors";

const RoutingVehicleService = ({
  vehicleData,
  isLoading,
  vehicleId: serialNo,
}) => {
  const { device: { vehicleKey = null } = {} } = vehicleData || {};

  const [fetchData, setFetchData] = useState(false);

  const defaultStartDate = new Date(new Date().setHours(0, 0, 0, 0));
  const defaultEndDate = new Date(new Date().setHours(23, 59, 59, 0));
  const minStartDate = new Date();
  minStartDate.setFullYear(minStartDate.getFullYear() - 1);

  const [rows, setRows] = useState([]);
  const [logsStartDate, setLogsStartDate] = useState(defaultStartDate);
  const [logsEndDate, setLogsEndDate] = useState(defaultEndDate);
  const [asOnDate, setAsOnDate] = useState(null);
  const [nextToken, setNextToken] = useState(null);

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ justifyContent: "flex-end" }}>
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{
            fileName: serialNo + "_ProxyLogs",
          }}
        />
      </GridToolbarContainer>
    );
  }

  function CustomFooter() {
    return (
      <GridFooterContainer
        style={{
          padding: "0 20px",
          color: "#2e2e2e",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        Total: {rows?.length} {asOnDate && <span>As On: {asOnDate}</span>}
        {nextToken && (
          <Button startIcon={<RefreshIcon />} onClick={() => loadMore()}>
            Load more
          </Button>
        )}
      </GridFooterContainer>
    );
  }

  const { data = [] } = useQuery(
    ["RoutingVehicleService", vehicleKey],
    getVehicleServices,
    {
      enabled: !!vehicleKey,
      refetchOnWindowFocus: false,
    },
  );

  const { data: serviceContractAuditData = [] } = useQuery(
    ["RoutingServiceContract", vehicleKey],
    getServiceContractAuditLogs,
    {
      enabled: !!vehicleKey,
      refetchOnWindowFocus: false,
    },
  );

  const { isLoading: logsLoading, isFetching } = useQuery(
    ["routingProxyLogs", serialNo, logsStartDate, logsEndDate, nextToken],
    getRoutingProxyLogs,
    {
      enabled: fetchData,
      onSuccess: ({ data }) => {
        const rowData = data?.events.map((row) => {
          return { ...row, id: crypto.randomUUID() };
        });
        setRows((rows) => [...rows, ...rowData]);
        if (data?.asOn !== null) {
          const asOn = moment(new Date(data?.asOn)).format(
            "DD-MMM-yyyy hh:mm:ss A",
          );
          setAsOnDate(asOn);
        }
        setNextToken(data?.nextToken);
      },
      onSettled: () => {
        setFetchData(false);
      },
      refetchOnWindowFocus: false,
    },
  );

  const serviceContractAudit = serviceContractAuditData?.data || [];

  const vehicleServices = data?.data;

  const processSearch = () => {
    setNextToken(null);
    setRows([]);
    setAsOnDate(null);
    setFetchData(true);
  };

  const loadMore = () => {
    setFetchData(true);
  };

  const logsGridColumns = [
    {
      field: "timestamp",
      headerName: "Timestamp",
      flex: 0.6,
      sortable: false,
      renderCell: (data) => (
        <Box>
          <Typography sx={{ fontSize: "1em" }}>
            {formatDateWithoutTime(data?.row?.timestamp)}
          </Typography>
          <Typography sx={{ fontSize: "0.9em", color: grey[600] }}>
            {formatTimeWithAmPm(data?.row?.timestamp)}
          </Typography>
        </Box>
      ),
    },
    { field: "vin", headerName: "VehicleKey", flex: 2, sortable: false },
    {
      field: "timeTaken",
      headerName: "Time Taken",
      flex: 0.5,
      sortable: false,
    },
    { field: "sourceIp", headerName: "Source IP", flex: 1, sortable: false },
    { field: "status", headerName: "Status", flex: 1, sortable: false },
    { field: "bytes", headerName: "Bytes", flex: 0.5, sortable: false },
    {
      field: "destinationIp",
      headerName: "Destination Ip",
      flex: 1,
      sortable: false,
    },
    {
      field: "rsContract",
      headerName: "Routing",
      flex: 0.3,
      sortable: false,
      renderCell: ({ row }) => {
        return row.rsContract === "VALID" ? (
          <CheckIcon style={{ color: "green" }} />
        ) : (
          <CancelIcon style={{ color: "red" }} />
        );
      },
    },
    {
      field: "telematicsContract",
      headerName: "Telematics",
      flex: 0.3,
      sortable: false,
      renderCell: ({ row }) => {
        return row.telematicsContract === "VALID" ? (
          <CheckIcon style={{ color: "green" }} />
        ) : (
          <CancelIcon style={{ color: "red" }} />
        );
      },
    },
    {
      field: "data",
      headerName: "Data",
      flex: 0.3,
      sortable: false,
      renderCell: ({ row }) => {
        return row.data === "ENABLED" ? (
          <CheckIcon style={{ color: "green" }} />
        ) : (
          <CancelIcon style={{ color: "red" }} />
        );
      },
    },
  ];

  return (
    <Box>
      <Grid container sx={{ pt: "30px" }}>
        <Grid item xs={6} sx={{ p: "12px" }}>
          <Card>
            <CardHeader
              title="Service Contract"
              titleTypographyProps={{ variant: "subtitle2" }}
              sx={{
                backgroundColor: (theme) => theme.palette.tertiary,
              }}
            />
            <CardContent>
              <Box sx={{ width: "100%" }}>
                <Grid container>
                  <Grid
                    item
                    xs={6}
                    sx={{ borderRight: 1, borderColor: "divider" }}
                  >
                    <List>
                      <ListItem {...overviewListItemStyles}>
                        <ListItemText
                          primary="RS Contract"
                          secondary={
                            vehicleServices?.routingServerContractStatus
                              ? vehicleServices?.routingServerContractStatus
                              : "-"
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={6}>
                    <List>
                      <ListItem {...overviewListItemStyles}>
                        <ListItemText
                          primary="Telematics Contract"
                          secondary={
                            vehicleServices?.telematicsContractStatus
                              ? vehicleServices?.telematicsContractStatus
                              : "-"
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sx={{ p: "12px" }}>
          <Card>
            <CardHeader
              title="Service Data"
              titleTypographyProps={{ variant: "subtitle2" }}
              sx={{
                backgroundColor: (theme) => theme.palette.tertiary,
              }}
            />
            <CardContent>
              <Box sx={{ width: "100%" }}>
                <Grid container>
                  <Grid
                    item
                    xs={6}
                    sx={{ borderRight: 1, borderColor: "divider" }}
                  >
                    <List>
                      <ListItem {...overviewListItemStyles}>
                        <ListItemText
                          primary="Data Status"
                          secondary={
                            vehicleServices?.dataEnabled
                              ? vehicleServices?.dataEnabled
                              : "ENABLED"
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={3}>
                    <List>
                      <ListItem {...overviewListItemStyles}>
                        <ListItemText
                          primary="Data Usage"
                          secondary={
                            vehicleServices?.dataUsage
                              ? `${vehicleServices?.dataUsage}%`
                              : "-"
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {serviceContractAudit?.length > 0 && (
        <div style={{ marginTop: "32px" }}>
          <Typography
            sx={{ flex: "1 1 100%", ml: "12px" }}
            variant="button"
            id="tableTitle"
            component="div"
          >
            Service Contract Audit Logs
          </Typography>

          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Contract Action</TableCell>
                  <TableCell>RS Contract</TableCell>
                  <TableCell>Telematics Contract</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {serviceContractAudit.map((row) => (
                  <TableRow
                    key={row?.auditId}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell>{formatDate_12h(row?.createdAt)}</TableCell>
                    <TableCell>{row?.contractAction}</TableCell>
                    <TableCell>{row?.routingServerContractStatus}</TableCell>
                    <TableCell>{row?.telematicsContractStatus}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
      <div style={{ marginTop: "32px" }}>
        <Typography
          sx={{ flex: "1 1 100%", ml: "12px" }}
          variant="button"
          id="tableTitle"
          component="div"
        >
          Routing Proxy Logs
        </Typography>
        <div style={{ marginTop: "25px" }}>
          <Grid container spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xl={2.2} lg={3} md={4} sm={6} xs={12}>
                <DateTimePicker
                  label="Start Date"
                  value={logsStartDate}
                  minDate={minStartDate}
                  disableFuture
                  onChange={(d) => setLogsStartDate(d)}
                  renderInput={(params) => (
                    <TextField
                      variant="standard"
                      required
                      type="date"
                      fullWidth
                      {...params}
                    />
                  )}
                />
              </Grid>
              <Grid item xl={2.2} lg={3} md={4} sm={6} xs={12}>
                <DateTimePicker
                  label="End Date"
                  value={logsEndDate}
                  disableFuture
                  onChange={(d) => setLogsEndDate(d)}
                  minDateTime={logsStartDate}
                  renderInput={(params) => (
                    <TextField
                      variant="standard"
                      required
                      type="date"
                      fullWidth
                      {...params}
                    />
                  )}
                />
              </Grid>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <Button
                  sx={{ ml: "8px", margin: "6px 20px 6px 30px" }}
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => processSearch()}
                >
                  Search
                </Button>
              </Grid>
            </LocalizationProvider>
          </Grid>

          <Grid container style={{ marginTop: "32px" }}>
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <DataGrid
                loading={logsLoading || isFetching}
                getRowId={(row) => row?.id}
                rows={rows || []}
                columns={logsGridColumns}
                components={{ Toolbar: CustomToolbar, Footer: CustomFooter }}
                autoHeight
                hideFooterPagination
                disableSelectionOnClick
                disableColumnFilter
                pageSize={-1}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </Box>
  );
};

export default RoutingVehicleService;
